import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  Typography
} from '@mui/material';

interface CreatePostFormProps {
  open: boolean;
  isAdmin?: boolean;
  onClose: () => void;
  onSubmit: (postData: PostFormData) => Promise<void>;
}

export interface PostFormData {
  title: string;
  content: string;
  category: 'general' | 'transportation' | 'accommodations' | 'other';
  is_announcement?: boolean;
}

export const CreatePostForm: React.FC<CreatePostFormProps> = ({
  open,
  isAdmin = false,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<PostFormData>({
    title: '',
    content: '',
    category: 'general',
    is_announcement: false
  });
  const [errors, setErrors] = useState<{ title?: string; content?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: { title?: string; content?: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else if (formData.content.length > 5000) {
      newErrors.content = 'Content must be 5000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      await onSubmit(formData);
      handleClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      is_announcement: false
    });
    setErrors({});
    setSubmitError(null);
    onClose();
  };

  const handleTitleChange = (value: string) => {
    setFormData({ ...formData, title: value });
    if (errors.title) {
      setErrors({ ...errors, title: undefined });
    }
  };

  const handleContentChange = (value: string) => {
    setFormData({ ...formData, content: value });
    if (errors.content) {
      setErrors({ ...errors, content: undefined });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Post</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            error={!!errors.title}
            helperText={errors.title || `${formData.title.length}/200 characters`}
            margin="normal"
            required
            autoFocus
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              label="Category"
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="transportation">Transportation</MenuItem>
              <MenuItem value="accommodations">Accommodations</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Content"
            value={formData.content}
            onChange={(e) => handleContentChange(e.target.value)}
            error={!!errors.content}
            helperText={errors.content || `${formData.content.length}/5000 characters`}
            margin="normal"
            multiline
            rows={8}
            required
            placeholder="Write your message here..."
          />

          {isAdmin && (
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_announcement || false}
                    onChange={(e) => setFormData({ ...formData, is_announcement: e.target.checked })}
                  />
                }
                label="Mark as Announcement"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                Announcements are displayed prominently at the top of the message board
              </Typography>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Tips:</strong>
            </Typography>
            <Typography variant="body2">
              • Choose the appropriate category to help others find your post
            </Typography>
            <Typography variant="body2">
              • Be clear and concise in your title
            </Typography>
            <Typography variant="body2">
              • Provide enough detail in the content for others to understand and respond
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !formData.title.trim() || !formData.content.trim()}
        >
          {submitting ? 'Creating...' : 'Create Post'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
